"use client";

import Image from "next/image";
import * as Clerk from "@clerk/elements/common";
import * as SignIn from "@clerk/elements/sign-in";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useSignIn } from "@clerk/nextjs";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import Spinner from "@/components/shared/spinner";
import { Lock, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function Page() {
  const searchParams = useSearchParams();
  const { signIn } = useSignIn();

  const errorCode = searchParams.get("__clerk_status");
  const externalAccountError = signIn?.firstFactorVerification?.error?.code;
  const showNoAccountError =
    errorCode === "sign_in_error" ||
    searchParams.get("error") === "identifier_not_found" ||
    externalAccountError === "external_account_not_found" ||
    externalAccountError === "identifier_not_found";

  return (
    <main className="flex min-h-screen w-full onboarding-bg">
      <div className="container relative grid flex-col items-center justify-center lg:max-w-none lg:grid-cols-2 lg:px-0">
        {/* Left panel — matches usehindsight.com brand */}
        <div
          className="relative hidden h-full flex-col overflow-hidden p-10 text-white lg:flex"
          style={{ backgroundColor: "#080F1E" }}
        >
          {/* Subtle grid */}
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />
          {/* Amber glow blobs */}
          <div
            className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full blur-3xl"
            style={{ backgroundColor: "rgba(217,119,6,0.18)" }}
          />
          <div
            className="absolute top-1/3 -right-20 h-64 w-64 rounded-full blur-3xl"
            style={{ backgroundColor: "rgba(217,119,6,0.08)" }}
          />

          {/* Logo */}
          <div className="relative z-20 flex items-center">
            <Image
              src="/logo_dark.png"
              alt="Hindsight"
              width={110}
              height={110}
            />
          </div>

          <div className="relative z-20 flex-1" />

          {/* Feature bullets + quote */}
          <div className="relative z-20 space-y-6 pb-4">
            <div className="space-y-3">
              {[
                "Analyze win/loss calls automatically",
                "Build battlecards from real deal data",
                "Track competitors across every deal",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <div
                    className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                    style={{ backgroundColor: "rgba(217,119,6,0.2)" }}
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path
                        d="M2 5l2.5 2.5L8 3"
                        stroke="#D97706"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="text-sm font-sora text-white/70">{item}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 pt-5">
              <p className="font-serif-display text-xl leading-snug text-white/90">
                &ldquo;Win more deals with competitive intelligence that works
                as fast as your sales cycle.&rdquo;
              </p>
              <div className="mt-4 flex items-center gap-3">
                <div className="flex -space-x-2">
                  <div className="h-7 w-7 rounded-full bg-amber-400 ring-2 ring-[#080F1E]" />
                  <div className="h-7 w-7 rounded-full bg-orange-400 ring-2 ring-[#080F1E]" />
                  <div className="h-7 w-7 rounded-full bg-yellow-400 ring-2 ring-[#080F1E]" />
                </div>
                <p className="text-sm font-sora text-white/45">
                  Trusted by product marketing and sales teams
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right panel */}
        <div className="flex items-center justify-center px-6 py-12 lg:px-8">
          <div className="w-full max-w-sm">
            <SignIn.Root>
              <Clerk.GlobalError>
                {({ message, code }) => (
                  <Alert variant="destructive" className="mb-4 rounded-xl">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {code === "form_identifier_not_found" ? (
                        <>
                          No account found with this email.{" "}
                          <Link
                            href="/sign-up"
                            className="font-medium underline underline-offset-4"
                          >
                            Sign up instead
                          </Link>
                        </>
                      ) : (
                        message
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </Clerk.GlobalError>

              <Clerk.Loading>
                {(isGlobalLoading) => (
                  <>
                    <SignIn.Step name="start">
                      <Card className="w-full rounded-2xl border border-[#efefed] shadow-[0_4px_24px_-4px_rgba(0,0,0,0.10),0_1px_4px_-1px_rgba(0,0,0,0.06)]">
                        <CardHeader className="pb-4 pt-6 px-6">
                          <CardTitle className="font-serif-display text-2xl font-normal text-slate-900">
                            Welcome back
                          </CardTitle>
                          <CardDescription className="font-sora text-slate-500 mt-1">
                            Sign in to your Hindsight account to continue.
                          </CardDescription>
                          {showNoAccountError && (
                            <Alert variant="destructive" className="mt-4 rounded-xl">
                              <AlertCircle className="h-4 w-4" />
                              <AlertDescription>
                                No account found with this email.{" "}
                                <Link
                                  href="/sign-up"
                                  className="font-medium underline underline-offset-4"
                                >
                                  Sign up instead
                                </Link>
                              </AlertDescription>
                            </Alert>
                          )}
                        </CardHeader>
                        <CardContent className="grid gap-y-3 px-6">
                          <div className="grid grid-cols-1 gap-y-2">
                            <Clerk.Connection name="microsoft" asChild>
                              <Button
                                variant="outline"
                                type="button"
                                disabled={isGlobalLoading}
                                className="rounded-xl border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all"
                              >
                                <Clerk.Loading scope="provider:microsoft">
                                  {(isLoading) =>
                                    isLoading ? (
                                      <Spinner />
                                    ) : (
                                      <>
                                        <Image
                                          src="/microsoft.svg"
                                          alt="Microsoft"
                                          width={16}
                                          height={16}
                                          className="mr-2"
                                          unoptimized={true}
                                        />
                                        Continue with Microsoft
                                      </>
                                    )
                                  }
                                </Clerk.Loading>
                              </Button>
                            </Clerk.Connection>
                            <Clerk.Connection name="google" asChild>
                              <Button
                                variant="outline"
                                type="button"
                                disabled={isGlobalLoading}
                                className="rounded-xl border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all"
                              >
                                <Clerk.Loading scope="provider:google">
                                  {(isLoading) =>
                                    isLoading ? (
                                      <Spinner />
                                    ) : (
                                      <>
                                        <Image
                                          src="/google.svg"
                                          alt="Google"
                                          width={18}
                                          height={18}
                                          className="mr-2"
                                          unoptimized={true}
                                        />
                                        Continue with Google
                                      </>
                                    )
                                  }
                                </Clerk.Loading>
                              </Button>
                            </Clerk.Connection>
                            <Button
                              variant="outline"
                              type="button"
                              disabled={isGlobalLoading}
                              asChild
                              className="rounded-xl border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all"
                            >
                              <Link href="/sign-in/sso">
                                <Lock className="mr-2 size-4 text-slate-400" />
                                Continue with SSO
                              </Link>
                            </Button>
                          </div>
                          <p className="flex items-center gap-x-3 text-xs text-slate-400 before:h-px before:flex-1 before:bg-slate-200 after:h-px after:flex-1 after:bg-slate-200">
                            or
                          </p>
                          <Clerk.Field name="identifier" className="space-y-2">
                            <Clerk.Label asChild>
                              <Label>Email address</Label>
                            </Clerk.Label>
                            <Clerk.Input type="email" required asChild>
                              <Input />
                            </Clerk.Input>
                            <Clerk.FieldError>
                              {({ message, code }) => (
                                <div className="text-sm text-destructive">
                                  {code === "form_identifier_not_found" ? (
                                    <>
                                      No account found with this email.{" "}
                                      <Link
                                        href="/sign-up"
                                        className="font-medium underline underline-offset-4"
                                      >
                                        Sign up instead
                                      </Link>
                                    </>
                                  ) : (
                                    message
                                  )}
                                </div>
                              )}
                            </Clerk.FieldError>
                          </Clerk.Field>
                        </CardContent>
                        <CardFooter className="px-6 pb-6 pt-2">
                          <div className="grid w-full gap-y-3">
                            <SignIn.Action submit asChild>
                              <Button
                                disabled={isGlobalLoading}
                                className="w-full rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-medium transition-all"
                              >
                                <Clerk.Loading>
                                  {(isLoading) =>
                                    isLoading ? <Spinner /> : "Continue"
                                  }
                                </Clerk.Loading>
                              </Button>
                            </SignIn.Action>
                            <Button
                              variant="link"
                              size="sm"
                              asChild
                              className="text-slate-500 hover:text-slate-700"
                            >
                              <Link href="/sign-up">
                                Don&apos;t have an account? Sign up
                              </Link>
                            </Button>
                          </div>
                        </CardFooter>
                      </Card>
                    </SignIn.Step>

                    <SignIn.Step name="choose-strategy">
                      <Card className="w-full rounded-2xl border border-[#efefed] shadow-[0_4px_24px_-4px_rgba(0,0,0,0.10),0_1px_4px_-1px_rgba(0,0,0,0.06)]">
                        <CardHeader className="pb-4 pt-6 px-6">
                          <CardTitle className="font-serif-display text-2xl font-normal text-slate-900">
                            Use another method
                          </CardTitle>
                          <CardDescription className="font-sora text-slate-500 mt-1">
                            Facing issues? Choose any of these sign-in methods.
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="grid gap-y-2 px-6">
                          <SignIn.SupportedStrategy name="email_code" asChild>
                            <Button
                              type="button"
                              variant="outline"
                              disabled={isGlobalLoading}
                              className="rounded-xl border-slate-200 hover:bg-slate-50 transition-all"
                            >
                              Email code
                            </Button>
                          </SignIn.SupportedStrategy>
                          <SignIn.SupportedStrategy name="password" asChild>
                            <Button
                              type="button"
                              variant="outline"
                              disabled={isGlobalLoading}
                              className="rounded-xl border-slate-200 hover:bg-slate-50 transition-all"
                            >
                              Password
                            </Button>
                          </SignIn.SupportedStrategy>
                        </CardContent>
                        <CardFooter className="px-6 pb-6 pt-2">
                          <div className="grid w-full gap-y-3">
                            <SignIn.Action navigate="previous" asChild>
                              <Button
                                disabled={isGlobalLoading}
                                variant="outline"
                                className="w-full rounded-xl border-slate-200 hover:bg-slate-50 transition-all"
                              >
                                <Clerk.Loading>
                                  {(isLoading) =>
                                    isLoading ? <Spinner /> : "Go back"
                                  }
                                </Clerk.Loading>
                              </Button>
                            </SignIn.Action>
                          </div>
                        </CardFooter>
                      </Card>
                    </SignIn.Step>

                    <SignIn.Step name="verifications">
                      <SignIn.Strategy name="password">
                        <Card className="w-full rounded-2xl border border-[#efefed] shadow-[0_4px_24px_-4px_rgba(0,0,0,0.10),0_1px_4px_-1px_rgba(0,0,0,0.06)]">
                          <CardHeader className="pb-4 pt-6 px-6">
                            <CardTitle className="font-serif-display text-2xl font-normal text-slate-900">
                              Enter your password
                            </CardTitle>
                            <CardDescription className="font-sora text-slate-500 mt-1">
                              Welcome back, <SignIn.SafeIdentifier />
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="grid gap-y-4 px-6">
                            <Clerk.Field name="password" className="space-y-2">
                              <Clerk.Label asChild>
                                <Label>Password</Label>
                              </Clerk.Label>
                              <Clerk.Input type="password" asChild>
                                <Input />
                              </Clerk.Input>
                              <Clerk.FieldError className="block text-sm text-destructive" />
                            </Clerk.Field>
                          </CardContent>
                          <CardFooter className="px-6 pb-6 pt-2">
                            <div className="grid w-full gap-y-3">
                              <SignIn.Action submit asChild>
                                <Button
                                  disabled={isGlobalLoading}
                                  className="w-full rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-medium transition-all"
                                >
                                  <Clerk.Loading>
                                    {(isLoading) =>
                                      isLoading ? <Spinner /> : "Sign in"
                                    }
                                  </Clerk.Loading>
                                </Button>
                              </SignIn.Action>
                              <SignIn.Action navigate="choose-strategy" asChild>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="link"
                                  className="text-slate-500 hover:text-slate-700"
                                >
                                  Use another method
                                </Button>
                              </SignIn.Action>
                            </div>
                          </CardFooter>
                        </Card>
                      </SignIn.Strategy>

                      <SignIn.Strategy name="email_code">
                        <Card className="w-full rounded-2xl border border-[#efefed] shadow-[0_4px_24px_-4px_rgba(0,0,0,0.10),0_1px_4px_-1px_rgba(0,0,0,0.06)]">
                          <CardHeader className="pb-4 pt-6 px-6">
                            <CardTitle className="font-serif-display text-2xl font-normal text-slate-900">
                              Check your email
                            </CardTitle>
                            <CardDescription className="font-sora text-slate-500 mt-1">
                              We sent a 6-digit code to{" "}
                              <SignIn.SafeIdentifier />
                            </CardDescription>
                          </CardHeader>
                          <CardContent className="grid gap-y-4 px-6">
                            <Clerk.Field name="code">
                              <Clerk.Label className="sr-only">
                                Email verification code
                              </Clerk.Label>
                              <div className="grid items-center justify-center gap-y-2">
                                <div className="flex justify-center text-center">
                                  <Clerk.Input
                                    type="otp"
                                    autoSubmit
                                    className="flex justify-center has-[:disabled]:opacity-50"
                                    render={({ value, status }) => (
                                      <div
                                        data-status={status}
                                        className={cn(
                                          "relative flex size-11 items-center justify-center border-y border-r border-slate-200 text-base font-mono transition-all first:rounded-l-xl first:border-l last:rounded-r-xl",
                                          {
                                            "z-10 ring-2 ring-amber-600 ring-offset-background border-amber-400":
                                              status === "cursor" ||
                                              status === "selected",
                                          },
                                        )}
                                      >
                                        {value}
                                        {status === "cursor" && (
                                          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                            <div className="h-4 w-px animate-caret-blink bg-foreground duration-1000" />
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  />
                                </div>
                                <Clerk.FieldError className="block text-center text-sm text-destructive" />
                                <SignIn.Action
                                  asChild
                                  resend
                                  className="text-muted-foreground"
                                  fallback={({ resendableAfter }) => (
                                    <Button variant="link" size="sm" disabled>
                                      Didn&apos;t receive a code? Resend (
                                      <span className="tabular-nums">
                                        {resendableAfter}
                                      </span>
                                      )
                                    </Button>
                                  )}
                                >
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="text-slate-500 hover:text-slate-700"
                                  >
                                    Didn&apos;t receive a code? Resend
                                  </Button>
                                </SignIn.Action>
                              </div>
                            </Clerk.Field>
                          </CardContent>
                          <CardFooter className="px-6 pb-6 pt-2">
                            <div className="grid w-full gap-y-3">
                              <SignIn.Action submit asChild>
                                <Button
                                  disabled={isGlobalLoading}
                                  className="w-full rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-medium transition-all"
                                >
                                  <Clerk.Loading>
                                    {(isLoading) =>
                                      isLoading ? <Spinner /> : "Continue"
                                    }
                                  </Clerk.Loading>
                                </Button>
                              </SignIn.Action>
                              <SignIn.Action navigate="choose-strategy" asChild>
                                <Button
                                  size="sm"
                                  variant="link"
                                  className="text-slate-500 hover:text-slate-700"
                                >
                                  Use another method
                                </Button>
                              </SignIn.Action>
                            </div>
                          </CardFooter>
                        </Card>
                      </SignIn.Strategy>
                    </SignIn.Step>
                  </>
                )}
              </Clerk.Loading>
            </SignIn.Root>
          </div>
        </div>
      </div>
    </main>
  );
}
